import { faAward } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState } from "react";
import { publicUrls } from "../../../Api/Auth/AppUrls";
import { Link } from "react-router-dom";
import Button from "../../../Components/Common/Button";
import { useAuth } from "../../../Context/AuthContext";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { toast } from "react-toastify";
import { StaticPageApiUrls, modelTestUrls } from "../../../Api/Auth/ApiUrls";
import { noAuthfetcher, sendPostRequest } from "../../../Api/swrConfig";
import Modal from "../../../Components/Common/Modal";
import Input from "../../../Components/Common/Input";
import useToastError from "../../../hooks/useErrorToast";
import FreeSimulatorModal from "../../Student/Simulator/Components/FreeSimulatorModal";

const CIVIL_SIMULATOR_SLUG = "simulator?subscription=true";

const OurCourses = () => {
  const { categoryData } = useAuth();
  const { showErrorToast } = useToastError();
  const [isFreeTestModalOpen, setIsFreeTestModalOpen] = useState(false);
  const [selectedFreeTest, setSelectedFreeTest] = useState(null);
  const [freeTestEmail, setFreeTestEmail] = useState("");
  const [isFreeModalOpen, setIsFreeModalOpen] = useState(false);

  const { data: simulatorData } = useSWR(
    StaticPageApiUrls.info(CIVIL_SIMULATOR_SLUG),
    noAuthfetcher,
  );

  const freeTests = useMemo(
    () =>
      simulatorData?.data?.modelTest?.filter((test) => test.price === 0) || [],
    [simulatorData],
  );

  const { trigger: createFreeTestTaker, isMutating: isCreatingFreeTest } =
    useSWRMutation(modelTestUrls.createFreeModelTestTaker, sendPostRequest, {
      onSuccess: () => {
        sessionStorage.setItem("freeTestEmail", freeTestEmail);
        toast.success("Successfully registered for free test!");
        setIsFreeTestModalOpen(false);
        setFreeTestEmail("");
        setIsFreeModalOpen(true);
      },
      onError: (error) => {
        showErrorToast(error || "Failed to register. Please try again.");
      },
    });

  const handleOpenFreeTest = (freeTest) => {
    setSelectedFreeTest(freeTest);
    const savedEmail = sessionStorage.getItem("freeTestEmail");
    if (savedEmail) {
      setFreeTestEmail(savedEmail);
      setIsFreeModalOpen(true);
    } else {
      setIsFreeTestModalOpen(true);
    }
  };

  const handleCloseFreeModal = () => {
    setIsFreeModalOpen(false);
    setSelectedFreeTest(null);
  };

  const handleFreeTestSubmit = (e) => {
    e.preventDefault();
    if (!freeTestEmail) {
      return toast.error("Email is required");
    }
    createFreeTestTaker({
      modelTest: selectedFreeTest._id,
      email: freeTestEmail,
    });
  };

  // Create features array based on visible categories
  const features = useMemo(() => {
    if (!categoryData) return [];

    const categoryMap = {
      Civil: {
        icon: faAward,
        navigate: publicUrls.CivilCourses,
      },
      Mechanical: {
        icon: faAward,
        navigate: publicUrls.MechanicalCourses,
      },
    };

    return categoryData
      .filter((category) => category.visibility) // Only show visible categories
      .map((category, index) => ({
        id: String(index + 1).padStart(2, "0"),
        icon: categoryMap[category.title]?.icon || faAward,
        title: `FE ${category.title}`,
        navigate: categoryMap[category.title]?.navigate || "#",
      }));
  }, [categoryData]);

  // Don't render if no visible categories and no free tests
  if (features.length === 0 && freeTests.length === 0) {
    return null;
  }

  return (
    <div className="sm:mt-24 mt-12 bg-gradient-to-br from-[#eaf1f5] via-[#d9e3ec] to-[#cfdbe6] py-20">
      <p className="text-2xl font-semibold text-[#2a4258] text-center uppercase tracking-wider">
        Our Courses
      </p>
      <h2 className="text-4xl font-bold text-[#2a4258] text-center mt-3 leading-tight">
        One Platform,
        <br /> Many Options to Explore
      </h2>

      <div className="pt-12 px-4">
        <div className="container mx-auto flex flex-col items-center">
          <div className="flex flex-wrap justify-center gap-12 items-stretch">
            {features.map((feature) => (
              <Link
                to={feature.navigate}
                key={feature.id}
                className="flex w-72 min-h-[280px]"
              >
                <div className="relative bg-[#f6f9fb] rounded-lg p-8 text-center border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:border-[#2a4258] group hover:rounded-tr-[60px] flex flex-col flex-1 w-full transform hover:scale-105">
                  <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-br from-[#2a4258] to-[#3c5c77] text-white shadow-lg transition-all duration-300 group-hover:scale-110 shrink-0">
                    <FontAwesomeIcon icon={feature.icon} className="text-3xl" />
                  </div>
                  <div className="flex flex-col flex-1 min-h-0">
                    <h3 className="text-xl font-semibold text-[#2a4258] group-hover:text-[#1e3246] transition duration-300">
                      {feature.title}
                    </h3>
                    <Button className="mt-auto pt-4 px-4 py-2 bg-[#2a4258] text-white rounded-full text-sm font-medium transition hover:bg-[#1e3246] w-fit mx-auto">
                      View Course
                    </Button>
                  </div>
                </div>
              </Link>
            ))}

            {/* Free simulator cards - same design and button alignment */}
            {freeTests.map((test) => (
              <div
                key={test._id}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenFreeTest(test)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenFreeTest(test);
                  }
                }}
                className="relative bg-[#f6f9fb] rounded-lg p-8 text-center border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:border-[#2a4258] group hover:rounded-tr-[60px] flex flex-col w-72 min-h-[280px] transform hover:scale-105 cursor-pointer"
              >
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-gradient-to-br from-[#2a4258] to-[#3c5c77] text-white shadow-lg transition-all duration-300 group-hover:scale-110 shrink-0">
                  <span className="text-xs font-bold">FREE</span>
                </div>
                <div className="flex flex-col flex-1 min-h-0">
                  <h3 className="text-xl font-semibold text-[#2a4258] group-hover:text-[#1e3246] transition duration-300">
                    {test.title}
                  </h3>
                  {test.duration != null && (
                    <p className="text-sm text-gray-600 mt-1">
                      {test.duration} hour{test.duration !== 1 ? "s" : ""}
                    </p>
                  )}
                  <Button className="mt-auto pt-4 px-4 py-2 bg-[#2a4258] text-white rounded-full text-sm font-medium transition hover:bg-[#1e3246] w-fit mx-auto">
                    Start Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Free Test Email Modal */}
      <Modal
        isOpen={isFreeTestModalOpen}
        title={selectedFreeTest?.title || "Free Model Test"}
        onCancel={() => {
          setIsFreeTestModalOpen(false);
          setSelectedFreeTest(null);
          setFreeTestEmail("");
        }}
        formWidth="sm:max-w-[50%]"
        footer={false}
      >
        <form onSubmit={handleFreeTestSubmit} className="space-y-4 p-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-bold text-green-800 mb-2">
              🎉 Free Model Test Access
            </h3>
            <p className="text-gray-700">
              You're about to start:{" "}
              <span className="font-semibold">{selectedFreeTest?.title}</span>
            </p>
          </div>

          {selectedFreeTest && (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">
                  Duration:
                </span>
                <span className="font-semibold text-gray-900">
                  {selectedFreeTest.duration} hours
                </span>
              </div>
              {selectedFreeTest.description && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-600">
                    Description:
                  </span>
                  <p className="text-gray-900 mt-1">
                    {selectedFreeTest.description}
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={freeTestEmail}
              onChange={(_, value) => setFreeTestEmail(value)}
              placeholder="Enter your email"
              className="w-full"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              We'll use this email to send you details.
            </p>
          </div>

          <div className="pt-4">
            <Button
              type="primary"
              className="w-full"
              disabled={isCreatingFreeTest}
            >
              {isCreatingFreeTest ? "Processing..." : "Continue to Test"}
            </Button>
          </div>
        </form>
      </Modal>

      <FreeSimulatorModal
        selected={selectedFreeTest}
        isOpen={isFreeModalOpen}
        onClose={handleCloseFreeModal}
      />
    </div>
  );
};

export default OurCourses;
